<template>
    <Suspense>
        <Resolver>
            <component :is="rootComponent" />
        </Resolver>
        <template #fallback>
            <MkLoading />
        </template>
    </Suspense>
    <Suspense>
        <Common />
    </Suspense>
</template>
<script setup lang="ts">
import {
    defineAsyncComponent,
    inject,
    markRaw,
    nextTick,
    onWatcherCleanup,
    readonly,
    shallowRef,
    watch,
    watchEffect,
    watchPostEffect,
    watchSyncEffect,
} from "vue";
import { magStreamingUrl, url, version } from "@/config";
import { useAccountEventBus, useAccountStore } from "@/model/account";
import { storeToRefs } from "pinia";
import { $tr } from "@/i18n";
import { differenceInHours } from "date-fns";
import { magToast, ToastAdapterString } from "@/composables/mag-toast";
import { applyTheme } from "@/scripts/theme";
import { useDeviceKind } from "@/composables/use-device-kind";
import {
    provideLocal,
    useEventListener,
    useIntervalFn,
    usePreferredDark,
} from "@vueuse/core";
import { useDevicePreferencesStore } from "@/store/device-preferences-store";
import { fetchInstance, instance } from "@/instance";
import { compareVersions } from "compare-versions";
import { makeHotkey } from "@/scripts/hotkey";
import { refChildren, refProp } from "@/composables/reactive";
import { useSettingsStore } from "./store/settings-store";
import * as Misskey from "calckey-js";
import { MagEventChannel } from "magnetar-common";
import Common from "@/layouts/main/common.vue";
import { useAsyncDependency } from "@/composables/mag-async-components";
import { mainRouter } from "@/router";
import { debounce } from "throttle-debounce";
import { openModalWindow, openPostForm } from "@/composables/modal";
import { messageAlert } from "@/composables/message-box";
import { validateTheme } from "@/store/theme-store";

const accountStore = useAccountStore();
const { currentUser, currentUserId, isLoggedIn, current } =
    storeToRefs(accountStore);

const devicePrefsStore = useDevicePreferencesStore();
const { preferences } = storeToRefs(devicePrefsStore);
const { syncDeviceDarkMode, darkTheme, lightTheme, themeInitial, theme } =
    refChildren(preferences);

const settingsStore = useSettingsStore();
const { settings } = storeToRefs(settingsStore);
const showUpdates = refProp(settings, "showUpdates");
const darkMode = refProp(settings, "darkMode");
const useBlurEffectForModal = refProp(settings, "useBlurEffectForModal");
const useBlurEffect = refProp(settings, "useBlurEffect");

const preferredDark = usePreferredDark();

const loadHandler = inject<() => void>("app-loaded-handler");

const Resolver = useAsyncDependency(async () => {
    await mainRouter.isReady();
    loadHandler?.();
});

const getRootComponent = () => {
    return window.location.search === "?zen"
        ? defineAsyncComponent({
              loader: () => import("./layouts/main/zen.vue"),
          })
        : !currentUser.value
          ? defineAsyncComponent({
                loader: () => import("./layouts/visitor/primary.vue"),
            })
          : defineAsyncComponent({
                loader: () => import("./layouts/main/universal.vue"),
            });
};

const rootComponent = shallowRef(getRootComponent());

watch(
    currentUserId,
    () => {
        rootComponent.value = getRootComponent();
    },
    { flush: "post" },
);

fetchInstance().then(() => {
    localStorage.setItem("v", instance.version);

    const lastVersion = localStorage.getItem("lastVersion");

    if (lastVersion !== version) {
        localStorage.setItem("lastVersion", version);
        theme.value = null;

        try {
            if (
                lastVersion != null &&
                compareVersions(version, lastVersion) === 1 &&
                showUpdates.value
            ) {
                if (accountStore.currentUser) {
                    nextTick(() => {
                        openModalWindow(
                            {
                                dismissable: true,
                            },
                            {
                                title: $tr("misskeyUpdated"),
                            },
                            defineAsyncComponent(
                                () => import("@/components/MkUpdated.vue"),
                            ),
                            ({}) => ({}),
                        );
                    });
                }
            }
        } catch (err) {
            console.error(err);
        }
    }

    if (themeInitial.value) {
        if (
            instance.defaultLightTheme != null &&
            validateTheme(instance.defaultLightTheme)
        ) {
            lightTheme.value = JSON.parse(instance.defaultLightTheme);
        }

        if (
            instance.defaultDarkTheme != null &&
            validateTheme(instance.defaultDarkTheme)
        ) {
            darkTheme.value = JSON.parse(instance.defaultDarkTheme);
        }

        themeInitial.value = false;
    }
});

const lastUsed = localStorage.getItem("lastUsed");
if (lastUsed && isLoggedIn.value) {
    const lastUsedDate = parseInt(lastUsed, 10);

    if (differenceInHours(Date.now(), lastUsedDate) > 4) {
        magToast(
            {
                value: $tr("welcomeBackWithName", {
                    name:
                        currentUser.value!.display_name ||
                        currentUser.value!.username,
                }),
            },
            ToastAdapterString,
            { lifetime: 15000 },
        );
    }
}

useIntervalFn(
    () => {
        if (isLoggedIn) {
            localStorage.setItem("lastUsed", Date.now().toString());
        }
    },
    60 * 1000,
    { immediate: true },
);

// FIXME
if (currentUser.value?.is_deleted) {
    messageAlert({
        type: "warning",
        text: $tr("accountDeletionInProgress"),
    });
}

watch(
    current,
    async (newAcc, oldAcc) => {
        if (newAcc === oldAcc || !newAcc) {
            return;
        }

        await accountStore.fetchCurrent();
    },
    {
        immediate: true,
    },
);

watchSyncEffect(() => {
    const light = lightTheme.value;
    const dark = darkTheme.value;

    const theme = darkMode.value ? dark : light;

    devicePrefsStore.$patch((state) => {
        Object.assign(state.preferences, {
            theme,
            colorScheme: darkMode.value ? "dark" : "light",
        });
    });
});

watch(
    () => devicePrefsStore.preferences.theme,
    (theme) => {
        if (!theme) {
            return;
        }

        applyTheme(theme);
    },
    {
        immediate: true,
    },
);

watchPostEffect(() => {
    const sync = syncDeviceDarkMode.value;
    const prefersDark = preferredDark.value;

    requestAnimationFrame(() => {
        if (sync) {
            darkMode.value = prefersDark;
        }
    });
});

watch(
    useBlurEffectForModal,
    (v) => {
        document.documentElement.style.setProperty(
            "--modalBgFilter",
            v ? "blur(4px)" : "none",
        );
    },
    { immediate: true },
);

const deviceKind = useDeviceKind();

watchEffect((v) => {
    if (useBlurEffect.value && deviceKind.value !== "smartphone") {
        document.documentElement.style.removeProperty("--blur");
        document.documentElement.style.setProperty(
            "--blurBg",
            "var(--acrylicBg)",
        );
    } else {
        document.documentElement.style.setProperty("--blur", "none");
        document.documentElement.style.setProperty("--blurBg", "var(--bg)");
    }
});

const hotkeys = {
    d: (): void => {
        darkMode.value = !darkMode.value;
    },
    "p|n": openPostForm,
};

watch(
    currentUserId,
    (val) => {
        if (!val) {
            return;
        }

        const abort = new AbortController();

        settingsStore.fetchConfig(abort);

        onWatcherCleanup(() => {
            abort.abort();
        });
    },
    {
        immediate: true,
    },
);

const syncDebounce = debounce(2000, (token: string) => {
    settingsStore.syncUpstream(token);
});

settingsStore.$subscribe(() => {
    if (!current.value) {
        return;
    }

    syncDebounce(current.value);
});

const legacyStream = shallowRef<Misskey.Stream | null>(null);
const stream = shallowRef<MagEventChannel | null>(null);

watch(
    current,
    (token) => {
        legacyStream.value?.close();
        stream.value?.close();

        if (!token) {
            legacyStream.value = null;
            stream.value = null;
            return;
        }

        legacyStream.value = markRaw(
            new Misskey.Stream(url, {
                token,
            }),
        );
        stream.value = markRaw(new MagEventChannel(magStreamingUrl, token));
    },
    {
        immediate: true,
    },
);

provideLocal("stream", readonly(stream));
provideLocal("legacyStream", readonly(legacyStream));

watchEffect(() => {
    document.documentElement.style.setProperty(
        "--link-text-decoration",
        settingsStore.settings.underlineLinks ? "dotted underline" : "none",
    );

    document.documentElement.style.setProperty(
        "--link-text-decoration-hover",
        settingsStore.settings.underlineLinks ? "solid underline" : "none",
    );
});

window.setTimeout(heartbeat, 1000 * 60);

function heartbeat(): void {
    if (legacyStream != null && document.visibilityState === "visible") {
        legacyStream.value?.send("ping");
    }
    window.setTimeout(heartbeat, 1000 * 60);
}

// TODO: Listen to remote preference store changes

useEventListener(document, "keydown", makeHotkey(hotkeys));

useAccountEventBus(accountStore);
</script>
